import { POSTHOG_FEATURE_FLAG_SHELLS } from "@/lib/posthog/setup";

export const FEATURE_FLAG_ENV_KEYS = {
  dynamicServingEnabled: "SEOH_DYNAMIC_SERVING_ENABLED",
  personaPublicUrlsEnabled: "SEOH_PERSONA_PUBLIC_URLS_ENABLED",
  ampAdvertised: "SEOH_AMP_ADVERTISED",
  adultPersonaVisible: "SEOH_ADULT_PERSONA_VISIBLE",
  experienceLayerEnabled: "SEOH_EXPERIENCE_LAYER_ENABLED",
} as const;

export type FeatureFlagEnv = Readonly<Record<string, string | undefined>>;

export type FeatureFlags = {
  readonly dynamicServingEnabled: boolean;
  readonly personaPublicUrlsEnabled: boolean;
  readonly ampAdvertised: boolean;
  readonly adultPersonaVisible: boolean;
  readonly experienceLayerEnabled: boolean;
};

const TRUE_VALUES = new Set(["1", "true", "yes", "on", "enabled"]);
const FALSE_VALUES = new Set(["0", "false", "no", "off", "disabled"]);

export type PostHogFeatureFlagKey = (typeof POSTHOG_FEATURE_FLAG_SHELLS)[number]["key"];
export type PostHogFeatureFlagRepoHook = (typeof POSTHOG_FEATURE_FLAG_SHELLS)[number]["repoHook"];

export const POSTHOG_FEATURE_FLAG_KEYS = POSTHOG_FEATURE_FLAG_SHELLS.map(
  (flagShell) => flagShell.key,
) as readonly PostHogFeatureFlagKey[];

export const POSTHOG_FEATURE_FLAG_FALLBACKS = Object.freeze(
  Object.fromEntries(POSTHOG_FEATURE_FLAG_KEYS.map((key) => [key, false])),
) as Readonly<Record<PostHogFeatureFlagKey, false>>;

export const POSTHOG_FEATURE_FLAG_HOOKS = Object.freeze(
  Object.fromEntries(POSTHOG_FEATURE_FLAG_SHELLS.map((flagShell) => [flagShell.repoHook, flagShell.key])),
) as Readonly<Record<PostHogFeatureFlagRepoHook, PostHogFeatureFlagKey>>;

const POSTHOG_FEATURE_FLAG_KEY_SET = new Set<string>(POSTHOG_FEATURE_FLAG_KEYS);

export function getFeatureFlags(env: FeatureFlagEnv = process.env): FeatureFlags {
  return {
    dynamicServingEnabled: booleanFlag(env, FEATURE_FLAG_ENV_KEYS.dynamicServingEnabled, true),
    personaPublicUrlsEnabled: booleanFlag(env, FEATURE_FLAG_ENV_KEYS.personaPublicUrlsEnabled, true),
    ampAdvertised: booleanFlag(env, FEATURE_FLAG_ENV_KEYS.ampAdvertised, true),
    adultPersonaVisible: booleanFlag(env, FEATURE_FLAG_ENV_KEYS.adultPersonaVisible, true),
    experienceLayerEnabled: booleanFlag(env, FEATURE_FLAG_ENV_KEYS.experienceLayerEnabled, true),
  };
}

export function isDynamicServingEnabled(env: FeatureFlagEnv = process.env): boolean {
  return booleanFlag(env, FEATURE_FLAG_ENV_KEYS.dynamicServingEnabled, true);
}

export function isPersonaPublicUrlsEnabled(env: FeatureFlagEnv = process.env): boolean {
  return booleanFlag(env, FEATURE_FLAG_ENV_KEYS.personaPublicUrlsEnabled, true);
}

export function isAmpAdvertised(env: FeatureFlagEnv = process.env): boolean {
  return booleanFlag(env, FEATURE_FLAG_ENV_KEYS.ampAdvertised, true);
}

export function isAdultPersonaVisible(env: FeatureFlagEnv = process.env): boolean {
  return booleanFlag(env, FEATURE_FLAG_ENV_KEYS.adultPersonaVisible, true);
}

export function isExperienceLayerEnabled(env: FeatureFlagEnv = process.env): boolean {
  return booleanFlag(env, FEATURE_FLAG_ENV_KEYS.experienceLayerEnabled, true);
}

export function isPostHogFeatureFlagKey(value: string): value is PostHogFeatureFlagKey {
  return POSTHOG_FEATURE_FLAG_KEY_SET.has(value);
}

export function getPostHogFeatureFlagFallback(key: PostHogFeatureFlagKey): boolean {
  return POSTHOG_FEATURE_FLAG_FALLBACKS[key];
}

export function getPostHogFeatureFlagDefaults(): Readonly<Record<PostHogFeatureFlagKey, boolean>> {
  return { ...POSTHOG_FEATURE_FLAG_FALLBACKS };
}

export function getPostHogFeatureFlagKeyByHook(
  repoHook: PostHogFeatureFlagRepoHook,
): PostHogFeatureFlagKey {
  return POSTHOG_FEATURE_FLAG_HOOKS[repoHook];
}

function booleanFlag(env: FeatureFlagEnv, key: string, fallback: boolean): boolean {
  const normalized = env[key]?.trim().toLowerCase();

  if (!normalized) {
    return fallback;
  }

  if (TRUE_VALUES.has(normalized)) {
    return true;
  }

  if (FALSE_VALUES.has(normalized)) {
    return false;
  }

  return fallback;
}
